import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import "../../styles/admin/admin-detail.css";
import "../../styles/admin/admin-form.css";
import config from "../../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

export default function AdminPetDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [pet, setPet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadPet();
  }, [id]);

  const loadPet = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/pets/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo cargar el detalle de la mascota");
      }

      const data = responseText ? JSON.parse(responseText) : null;
      setPet(data);
    } catch (err) {
      setError(err.message || "Ocurrió un error al cargar la mascota");
      setPet(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleDateString("es-CO");
    } catch {
      return "-";
    }
  };

  const normalizeSpecies = (value) => {
    const v = (value || "").toLowerCase();
    if (v === "perro") return "Perro";
    if (v === "gato") return "Gato";
    return value || "-";
  };

  const normalizeSex = (value) => {
    const v = (value || "").toLowerCase();
    if (v === "macho") return "Macho";
    if (v === "hembra") return "Hembra";
    return value || "-";
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page-head">
          <div>
            <h2>Detalle de mascota</h2>
            <p>Cargando información...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !pet) {
    return (
      <div className="admin-page">
        <div className="admin-page-head">
          <div>
            <h2>Detalle de mascota</h2>
            <p>Información completa de la mascota</p>
          </div>
        </div>

        <p className="admin-form-message error">{error}</p>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="admin-page">
        <div className="admin-page-head">
          <div>
            <h2>Detalle de mascota</h2>
            <p>No se encontró información de la mascota</p>
          </div>
        </div>
      </div>
    );
  }

  const ownerName = `${pet.ownerName || ""} ${pet.ownerLastName || ""}`.trim();
  const photo =
    pet.profilePhotoUrl ||
    pet.cardImageUrl ||
    "https://via.placeholder.com/180x180.png?text=Mascota";

  return (
    <div className="admin-page">
      <div className="admin-page-head admin-detail-topbar">
        <div className="admin-detail-head-left">
          <button
            type="button"
            className="admin-back-arrow-btn"
            onClick={() => navigate("/admin/mascotas")}
            title="Volver"
          >
            <ArrowLeft size={20} />
          </button>

          <div>
            <h2>Detalle de mascota</h2>
            <p>Información completa de la mascota</p>
          </div>
        </div>
      </div>

      {error ? <p className="admin-form-message error">{error}</p> : null}

      <div className="detail-grid">
        <section className="detail-card">
          <h3>Datos generales</h3>
          <div className="pet-detail-top">
            <img src={photo} alt={pet.name || "Mascota"} className="pet-detail-photo" />
            <div className="detail-list">
              <p><strong>Nombre:</strong> {pet.name || "-"}</p>
              <p><strong>Especie:</strong> {normalizeSpecies(pet.species)}</p>
              <p><strong>Raza:</strong> {pet.breed || "-"}</p>
              <p><strong>Sexo:</strong> {normalizeSex(pet.sex)}</p>
              <p><strong>Peso:</strong> {pet.weightKg ? `${pet.weightKg} kg` : "-"}</p>
              <p><strong>Fecha de nacimiento:</strong> {formatDate(pet.birthDate)}</p>
            </div>
          </div>
        </section>

        <section className="detail-card">
          <h3>Propietario</h3>
          <div className="detail-list">
            <p><strong>Nombre:</strong> {ownerName || "-"}</p>
            <p><strong>Correo:</strong> {pet.ownerEmail || "-"}</p>
            <p><strong>Estado:</strong> {pet.ownerStatus || "-"}</p>
          </div>
        </section>

        <section className="detail-card">
          <h3>Vacunas</h3>
          <ul className="detail-ul">
            {pet.vaccines && pet.vaccines.length > 0 ? (
              pet.vaccines.map((vacuna, index) => (
                <li key={index}>{vacuna}</li>
              ))
            ) : (
              <li>Sin vacunas registradas</li>
            )}
          </ul>
        </section>

        <section className="detail-card">
          <h3>Eventos del calendario</h3>
          <ul className="detail-ul">
            {pet.events && pet.events.length > 0 ? (
              pet.events.map((evento, index) => (
                <li key={index}>{evento}</li>
              ))
            ) : (
              <li>Sin eventos registrados</li>
            )}
          </ul>
        </section>

        <section className="detail-card">
          <h3>Carnet</h3>
          <div className="detail-list">
            <p>{pet.carnet || "Sin carnet digital"}</p>
          </div>
        </section>
      </div>
    </div>
  );
}